import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, type Locale } from '@barev/i18n';
import { navigate, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, PlanBadge, Skeleton } from '../components/kit.tsx';

function CopyLink({ url }: { url: string }) {
  const { t } = useT();
  const toast = useToast();
  return (
    <div className="bo-linkbox">
      <code>{url}</code>
      <button
        type="button"
        className="bo-btn bo-btn--sm"
        onClick={() => {
          void navigator.clipboard?.writeText(url);
          toast(t('ui.copied'));
        }}
      >
        {t('ui.copy')}
      </button>
    </div>
  );
}
export { CopyLink };

const GIFT_MONTHS = [1, 2, 3, 6, 12] as const;
type GiftMonths = (typeof GIFT_MONTHS)[number];
const PLAN_NAME = { BAREV_PLUS: 'Barev+', ELITE: 'Barev Elite' } as const;

/** «Barev+ на 3 мес.» или «без подарка» */
function giftText(t: ReturnType<typeof useT>['t'], a: { giftPlan: 'BAREV_PLUS' | 'ELITE' | null; giftMonths: number }) {
  return a.giftPlan ? t('ag.giftValue', { plan: PLAN_NAME[a.giftPlan], n: a.giftMonths }) : t('ag.noGift');
}

export function Agencies() {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const list = useQuery(trpc.bo.partners.agencies.queryOptions());
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('ag.title')}</h1>
        <button type="button" className="bo-btn bo-btn--accent" onClick={() => setCreating(true)}>
          <I name="plus" size={16} />
          {t('ag.add')}
        </button>
      </div>
      {!list.data ? (
        <Skeleton h={240} />
      ) : list.data.length === 0 ? (
        <div className="bo-panel bo-empty">{t('ag.empty')}</div>
      ) : (
        <div className="bo-panel" style={{ padding: 8 }}>
          <table className="bo-table bo-ptable">
            <thead>
              <tr>
                <th>{t('ag.col.name')}</th>
                <th className="bo-r">{t('ag.col.clients')}</th>
                <th className="bo-r">{t('ag.col.gifts')}</th>
                <th className="bo-r">{t('ag.col.month')}</th>
                <th className="bo-r">{t('ag.col.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((a) => (
                <tr key={a.id} onClick={() => navigate(`/agencies/${a.id}`)} data-off={!a.active || undefined}>
                  <td>
                    <a href={`#/agencies/${a.id}`} className="bo-who" onClick={(e) => e.stopPropagation()}>
                      <Avatar name={a.name} id={a.id} size={38} />
                      <span style={{ minWidth: 0 }}>
                        <span className="bo-who__name">{a.name}</span>
                        <span className="bo-who__meta">{[a.code, `${a.commissionPct}%`, giftText(t, a), a.sales?.name].filter(Boolean).join(' · ')}</span>
                        {!a.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
                      </span>
                    </a>
                  </td>
                  <td className="bo-r bo-num">{a.clients}</td>
                  <td className="bo-r bo-num">{a.gifts}</td>
                  <td className="bo-r bo-num">{amd(a.thisMonth)}</td>
                  <td className="bo-r">
                    <b className="bo-num">{amd(a.balance)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="bo-hint" style={{ padding: '10px 12px 4px' }}>
            {t('ag.hint')}
          </p>
        </div>
      )}
      {creating && <AgencyDialog onClose={() => setCreating(false)} />}
    </>
  );
}

export function AgencyCard({ id }: { id: string }) {
  const { t, amd, date, locale } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const a = useQuery(trpc.bo.partners.agency.queryOptions({ id }));
  const [dlg, setDlg] = useState<'edit' | 'payout' | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.partners.agency.queryKey({ id }) });
    qc.invalidateQueries({ queryKey: trpc.bo.partners.agencies.queryKey() });
  };
  const back = (
    <a className="bo-back" href="#/agencies">
      <I name="back" size={16} />
      {t('ag.back')}
    </a>
  );
  if (!a.data)
    return (
      <>
        {back}
        {a.isError ? <div className="bo-panel bo-empty">{errText(t, a.error)}</div> : <Skeleton h={300} />}
      </>
    );
  const d = a.data;
  return (
    <>
      {back}
      <header className="bo-profile">
        <Avatar name={d.name} id={d.id} size={64} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 className="bo-title" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
            {d.name}
          </h1>
          <div className="bo-muted" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span className="bo-num">{d.code}</span>
            <span>{t('ag.pct', { n: d.commissionPct })}</span>
            <span>🎁 {giftText(t, d)}</span>
            {d.housewarmingPoints > 0 && <span>🪙 {t('ag.hwValue', { n: new Intl.NumberFormat('ru-RU').format(d.housewarmingPoints), days: d.housewarmingDays })}</span>}
            {d.sales && <span>{t('ag.byS', { name: d.sales.name })}</span>}
            <span>{t('prv.since', { date: date(d.createdAt) })}</span>
            {!d.active && <span className="bo-chip bo-chip--danger">{t('ag.inactive')}</span>}
          </div>
        </div>
        <button type="button" className="bo-btn" onClick={() => setDlg('edit')}>
          {t('prv.edit')}
        </button>
      </header>

      <div className="bo-client-grid">
        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ag.link')}</h2>
            </div>
            <p className="bo-hint" style={{ marginBottom: 10 }}>
              {t('ag.linkHint', { code: d.code })}
            </p>
            <CopyLink url={d.link} />
          </section>
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ag.clients')}</h2>
              <span className="bo-faint bo-small">
                {d.clients.length} · {t('ag.giftsN', { n: d.gifts })}
              </span>
            </div>
            {d.clients.length === 0 ? (
              <p className="bo-hint">{t('ag.noClients')}</p>
            ) : (
              <ul className="bo-list">
                {d.clients.map((c) => (
                  <li key={c.id}>
                    <a href={`#/clients/${c.id}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <b>{c.name}</b>
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        {c.attachedAt ? date(c.attachedAt) : '—'} · {t('ag.orders', { n: c.orders })}
                      </span>
                    </a>
                    <PlanBadge plan={c.plan} t={t} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <div className="bo-col">
          <section className="bo-panel bo-money-card">
            <span className="bo-label" style={{ color: 'inherit', opacity: 0.75 }}>
              {t('ag.toPay')}
            </span>
            <div className="bo-bignum" style={{ margin: '6px 0 8px' }}>
              {amd(d.balance)}
            </div>
            <div className="bo-small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <span>
                {t('ag.accrued')} <b className="bo-num">{amd(d.accrued)}</b>
              </span>
              <span>
                {t('prv.paidTotal')} <b className="bo-num">{amd(d.paid)}</b>
              </span>
            </div>
            <button type="button" className="bo-btn bo-btn--sm" disabled={d.balance <= 0} onClick={() => setDlg('payout')}>
              {t('prv.payout')}
            </button>
            {d.months.length > 0 && (
              <ul className="bo-list" style={{ marginTop: 12 }}>
                {d.months.map((m) => (
                  <li key={m.period}>
                    <span className="bo-grow" style={{ textTransform: 'capitalize' }}>
                      {formatDateTime(locale as Locale, new Date(`${m.period}-15T12:00:00+04:00`), { month: 'long', year: 'numeric' })}
                    </span>
                    <span className="bo-small" style={{ opacity: 0.75 }}>
                      {t('ag.orders', { n: m.orders })}
                    </span>
                    <b className="bo-num">{amd(m.amount)}</b>
                  </li>
                ))}
              </ul>
            )}
            {d.payouts.length > 0 && (
              <p className="bo-small" style={{ marginTop: 10, opacity: 0.8 }}>
                {t('ag.payouts')}: {d.payouts.slice(0, 5).map((x) => `${amd(x.amount)}${x.paidAt ? ` (${date(x.paidAt)})` : ''}`).join(', ')}
              </p>
            )}
          </section>
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('crm.contacts')}</h2>
            </div>
            <p style={{ margin: 0 }}>{d.contact ?? '—'}</p>
            {d.note && <p className="bo-side-panel__note" style={{ marginTop: 12 }}>{d.note}</p>}
          </section>
        </div>
      </div>
      {dlg === 'edit' && <AgencyDialog agency={d} onClose={() => setDlg(null)} onDone={refresh} />}
      {dlg === 'payout' && <AgencyPayout id={d.id} balance={d.balance} onClose={() => setDlg(null)} onDone={refresh} />}
    </>
  );
}

function AgencyDialog({ agency, onClose, onDone }: { agency?: { id: string; code: string; name: string; commissionPct: number; giftPlan: 'BAREV_PLUS' | 'ELITE' | null; giftMonths: number; housewarmingPoints: number; housewarmingDays: number; contact: string | null; note: string | null; active: boolean; sales: { id: string } | null }; onClose: () => void; onDone?: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const team = useQuery(trpc.bo.partners.salesTeam.queryOptions());
  const [f, setF] = useState({
    code: agency?.code ?? '',
    name: agency?.name ?? '',
    commissionPct: String(agency?.commissionPct ?? 10),
    giftPlan: (agency ? (agency.giftPlan ?? 'NONE') : 'BAREV_PLUS') as 'BAREV_PLUS' | 'ELITE' | 'NONE',
    giftMonths: (GIFT_MONTHS.includes(agency?.giftMonths as GiftMonths) ? agency!.giftMonths : 1) as GiftMonths,
    hwPoints: String(agency?.housewarmingPoints ?? 0),
    hwDays: String(agency?.housewarmingDays ?? 60),
    contact: agency?.contact ?? '',
    note: agency?.note ?? '',
    active: agency?.active ?? true,
    salesId: agency?.sales?.id ?? '',
  });
  const save = useMutation(
    trpc.bo.partners.saveAgency.mutationOptions({
      onSuccess: (r) => {
        qc.invalidateQueries({ queryKey: trpc.bo.partners.agencies.queryKey() });
        onDone?.();
        onClose();
        if (!agency) navigate(`/agencies/${r.id}`);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={agency ? t('ag.editTitle') : t('ag.add')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f.name.trim() || !f.code.trim() || save.isPending}
            onClick={() => save.mutate({ id: agency?.id, code: f.code, name: f.name, commissionPct: Math.min(100, Number(f.commissionPct) || 0), giftPlan: f.giftPlan === 'NONE' ? null : f.giftPlan, giftMonths: f.giftMonths, housewarmingPoints: Number(f.hwPoints) || 0, housewarmingDays: Number(f.hwDays) || 60, contact: f.contact || null, note: f.note || null, active: f.active, salesId: f.salesId || null })}
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('crm.name')}>
          <input className="bo-input" value={f.name} maxLength={100} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('crm.agencyCode')} hint={t('crm.agencyCodeHint')}>
          <input className="bo-input" value={f.code} maxLength={32} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('ag.pctLabel')}>
          <input className="bo-input" inputMode="numeric" value={f.commissionPct} onChange={(e) => setF({ ...f, commissionPct: e.target.value.replace(/\D/g, '') })} />
        </Field>
        <Field label={t('ag.sales')}>
          <select className="bo-select" value={f.salesId} onChange={(e) => setF({ ...f, salesId: e.target.value })}>
            <option value="">—</option>
            {team.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('ag.giftPlan')} hint={t('ag.giftHint')}>
          <select className="bo-select" value={f.giftPlan} onChange={(e) => setF({ ...f, giftPlan: e.target.value as typeof f.giftPlan })}>
            <option value="BAREV_PLUS">Barev+</option>
            <option value="ELITE">Barev Elite</option>
            <option value="NONE">{t('ag.noGift')}</option>
          </select>
        </Field>
        <Field label={t('ag.giftMonths')}>
          <select className="bo-select" value={f.giftMonths} disabled={f.giftPlan === 'NONE'} onChange={(e) => setF({ ...f, giftMonths: Number(e.target.value) as GiftMonths })}>
            {GIFT_MONTHS.map((m) => (
              <option key={m} value={m}>
                {t('ag.monthsN', { n: m })}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('ag.hwPoints')} hint={t('ag.hwHint')}>
          <input className="bo-input" inputMode="numeric" value={f.hwPoints} onChange={(e) => setF({ ...f, hwPoints: e.target.value.replace(/\D/g, '').slice(0, 7) })} />
        </Field>
        <Field label={t('ag.hwDays')}>
          <input className="bo-input" inputMode="numeric" value={f.hwDays} disabled={!Number(f.hwPoints)} onChange={(e) => setF({ ...f, hwDays: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
        </Field>
      </div>
      <Field label={t('crm.contacts')}>
        <input className="bo-input" value={f.contact} maxLength={200} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder={t('ag.contactPh')} />
      </Field>
      <Field label={t('crm.note')}>
        <textarea className="bo-textarea" style={{ minHeight: 64 }} value={f.note} maxLength={2000} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <label className="bo-check">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('ag.active')}
      </label>
    </Dialog>
  );
}

function AgencyPayout({ id, balance, onClose, onDone }: { id: string; balance: number; onClose: () => void; onDone: () => void }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [amount, setAmount] = useState(String(balance));
  const [note, setNote] = useState('');
  const save = useMutation(
    trpc.bo.partners.agencyPayout.mutationOptions({
      onSuccess: () => {
        onDone();
        toast(t('prv.payoutDone'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ag.payoutTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!Number(amount) || save.isPending} onClick={() => save.mutate({ id, amount: Number(amount), note: note || undefined })}>
            {t('prv.payoutConfirm')}
          </button>
        </>
      }
    >
      <Field label={t('prv.amount')} hint={t('ag.balanceHint', { sum: amd(balance) })}>
        <input className="bo-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label={t('prv.payoutNote')}>
        <input className="bo-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={t('prv.payoutNotePh')} />
      </Field>
    </Dialog>
  );
}
